import {
  BookOpenText,
  Calendar,
  CalendarCog,
  Contact,
  Home,
  LayoutTemplate,
  List,
  Pencil,
  School,
  Search,
  User,
  UserCheck,
  UserPen,
  Users,
} from "lucide-react"

import { WORKSPACE_OWNERS } from "@/components/workspace/definition"
import {
  defineWorkspacePages,
  workspacePageChrome,
} from "@/components/workspace/page-chrome"
import {
  editLearnersSchoolPreferenceTemplate,
  editLearnersSchoolSchoolProfile,
  editLearnersSchoolStudent,
  editLearnersSchoolStudentPreferences,
  editLearnersStudent,
  editLearnersStudentChangeWishlist,
  editLearnersStudentPreferences,
  learnersSchoolPreferenceTemplates,
  learnersSchoolReliefListingRequests,
  learnersSchoolReliefListings,
  learnersSchoolStudent,
  learnersSchoolStudentPreferences,
  learnersSchoolStudents,
  learnersSchoolWorkspace,
  learnersStudent,
  learnersStudentClasses,
  learnersStudentPreferences,
  learnersStudentWorkspace,
  newLearnersSchoolReliefListing,
  newLearnersSchoolStudent,
} from "@/routes"

// The Learning domain's pages, keyed by Inertia component name, with the
// static facts the shell reads before a page's code runs: its layout, and
// for a page with the band the trail its chrome walks. Each page belongs to
// the workspace its owner names from the page's route: a school's (schoolId)
// or a student's (studentId, or id on the student's own record pages). A
// page with no parent is a child of that workspace's root. A student seen
// from both has a page in each (learners/school_students/* under the school,
// learners/students/* the student's own), so every page has one fixed parent
// and one fixed owner. The pages opened in a sheet are bare: they draw their
// own body and walk no trail.
export const learnerPages = defineWorkspacePages({
  "learners/school_profiles/edit": {
    layout: "editor",
    owner: WORKSPACE_OWNERS.school,
    icon: School,
    title: "school_profiles:edit.title",
    href: ({ schoolId }) => editLearnersSchoolSchoolProfile({ schoolId }).url,
  },
  "learners/school_students/index": {
    layout: "standard",
    owner: WORKSPACE_OWNERS.school,
    icon: Users,
    title: "school_students:index.title",
    href: ({ schoolId }) => learnersSchoolStudents.index({ schoolId }).url,
  },
  "learners/school_students/new": {
    layout: "editor",
    owner: WORKSPACE_OWNERS.school,
    icon: User,
    title: "school_students:new.title",
    href: ({ schoolId }) => newLearnersSchoolStudent({ schoolId }).url,
    parent: "learners/school_students/index",
  },
  // A record page: its href is built from a child's path, where the student
  // is :studentId (preferences) or :id (edit).
  "learners/school_students/show": {
    layout: "standard",
    owner: WORKSPACE_OWNERS.school,
    icon: User,
    title: "students:show.title",
    href: ({ schoolId, studentId, id }) =>
      learnersSchoolStudent({ schoolId, id: studentId ?? id }).url,
    parent: "learners/school_students/index",
    record: "school_students:record",
  },
  "learners/school_students/edit": {
    layout: "editor",
    owner: WORKSPACE_OWNERS.school,
    icon: UserPen,
    title: "students:edit.title",
    href: ({ schoolId, id }) => editLearnersSchoolStudent({ schoolId, id }).url,
    parent: "learners/school_students/show",
  },
  "learners/school_students/preferences/show": {
    layout: "standard",
    owner: WORKSPACE_OWNERS.school,
    icon: Calendar,
    title: "preferences:show.title",
    href: ({ schoolId, studentId }) =>
      learnersSchoolStudentPreferences({ schoolId, studentId }).url,
    parent: "learners/school_students/show",
  },
  "learners/school_students/preferences/edit": {
    layout: "standard",
    owner: WORKSPACE_OWNERS.school,
    icon: CalendarCog,
    title: "preferences:edit.title",
    href: ({ schoolId, studentId }) =>
      editLearnersSchoolStudentPreferences({ schoolId, studentId }).url,
    parent: "learners/school_students/show",
  },
  "learners/preference_templates/index": {
    layout: "standard",
    owner: WORKSPACE_OWNERS.school,
    icon: List,
    title: "preference_templates:index.title",
    href: ({ schoolId }) => learnersSchoolPreferenceTemplates({ schoolId }).url,
  },
  "learners/preference_templates/edit": {
    layout: "standard",
    owner: WORKSPACE_OWNERS.school,
    icon: LayoutTemplate,
    title: "preference_templates:edit.title",
    href: ({ schoolId, id }) =>
      editLearnersSchoolPreferenceTemplate({ schoolId, id }).url,
    parent: "learners/preference_templates/index",
  },
  "learners/relief_listings/index": {
    layout: "standard",
    owner: WORKSPACE_OWNERS.school,
    icon: Contact,
    title: "school_relief_listings:index.title",
    href: ({ schoolId }) => learnersSchoolReliefListings({ schoolId }).url,
  },
  "learners/relief_listings/new": {
    layout: "editor",
    owner: WORKSPACE_OWNERS.school,
    icon: UserCheck,
    title: "school_relief_listings:new.title",
    href: ({ schoolId }) => newLearnersSchoolReliefListing({ schoolId }).url,
    parent: "learners/relief_listings/index",
  },
  "learners/relief_listings/requests/index": {
    layout: "standard",
    owner: WORKSPACE_OWNERS.school,
    icon: Contact,
    title: "school_relief_listings:requests.index.title",
    href: ({ schoolId, reliefListingId }) =>
      learnersSchoolReliefListingRequests({ schoolId, reliefListingId }).url,
    parent: "learners/relief_listings/index",
  },
  "learners/students/show": {
    layout: "standard",
    owner: WORKSPACE_OWNERS.studentRecord,
    icon: Search,
    title: "students:show.title",
    href: ({ studentId, id }) => learnersStudent({ id: studentId ?? id }).url,
  },
  "learners/students/edit": {
    layout: "editor",
    owner: WORKSPACE_OWNERS.studentRecord,
    icon: User,
    title: "students:edit.title",
    href: ({ id }) => editLearnersStudent({ id }).url,
  },
  "learners/preferences/show": {
    layout: "editor",
    owner: WORKSPACE_OWNERS.student,
    icon: Calendar,
    title: "preferences:show.title",
    href: ({ studentId }) => learnersStudentPreferences({ studentId }).url,
    parent: "learners/students/show",
  },
  "learners/preferences/edit": {
    layout: "editor",
    owner: WORKSPACE_OWNERS.student,
    icon: CalendarCog,
    title: "preferences:edit.title",
    href: ({ studentId }) => editLearnersStudentPreferences({ studentId }).url,
    parent: "learners/students/show",
  },
  "learners/students/classes/index": {
    layout: "standard",
    owner: WORKSPACE_OWNERS.student,
    icon: BookOpenText,
    title: "student_classes:title",
    href: ({ studentId }) => learnersStudentClasses({ studentId }).url,
  },
  "learners/change_wishlists/change_wishlists/edit": {
    layout: "standard",
    owner: WORKSPACE_OWNERS.student,
    icon: Pencil,
    title: "change_wishlists:edit.title",
    href: ({ studentId, id }) =>
      editLearnersStudentChangeWishlist({ studentId, id }).url,
    parent: "learners/students/classes/index",
  },

  // The two workspaces' home pages, the roots the trails above start from.
  "learners/workspaces/show": {
    layout: "standard",
    owner: WORKSPACE_OWNERS.school,
    icon: Home,
    title: "nav:school_admin_workspace_title",
    href: ({ schoolId }) => learnersSchoolWorkspace({ schoolId }).url,
    root: true,
  },
  "learners/students/workspaces/show": {
    layout: "standard",
    owner: WORKSPACE_OWNERS.student,
    icon: Home,
    title: "nav:student_workspace_title",
    href: ({ studentId }) => learnersStudentWorkspace({ studentId }).url,
    root: true,
  },

  "learners/change_wishlists/change_wishlist_items/new": { layout: "bare" },
  "learners/preference_templates/applications/new": { layout: "bare" },
  "learners/preference_templates/new": { layout: "bare" },
  "learners/relief_listings/applications/edit": { layout: "bare" },
  "learners/relief_listings/applications/new": { layout: "bare" },
  "learners/relief_listings/applications/show": { layout: "bare" },
  "learners/relief_listings/invitation_responses/new": { layout: "bare" },
  "learners/relief_listings/invitation_responses/show": { layout: "bare" },
  "learners/relief_listings/invitations/new": { layout: "bare" },
  "learners/relief_listings/invitations/show": { layout: "bare" },
})

export const LearnerPageChrome = workspacePageChrome(learnerPages)
